import React, { useEffect, useRef, useState } from 'react';
import { FileSignature, X } from 'lucide-react';
import Modal from '../../../components/modals/Modal';
import Button from '../../../components/ui/buttons/Button';
import LoadingSpinner from '../../../components/ui/loading/LoadingSpinner';
import { useToast } from '../../../components/ui/toast/ToastContext';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';
import { camelizeKeys } from '../../../utils/camelizeKeys';

const ENVELOPE_STATUS_LABELS = {
  created: 'Rascunho (não enviado)',
  sent: 'Enviado, aguardando assinaturas',
  delivered: 'Visualizado, aguardando assinaturas',
  completed: 'Assinado por todos',
  declined: 'Recusado',
  voided: 'Anulado',
};

const CONTRACT_KIND_LABELS = {
  complexity1: 'Padrão — Plano Complexidade 1',
  complexity2: 'Padrão — Plano Complexidade 2',
  spouse: 'Cônjuge',
  standard: 'Padrão',
};

const PLAN_NUMBER = { complexity1: 1, complexity2: 2 };

// camelizeKeys não mexe nos valores: o kind chega como 'complexity_1'
const normalizeKind = (kind) => kind && kind.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());

const formatCurrency = (value) => Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const signerSituation = (signer) => {
  if (signer.signedAt) return `Assinou em ${signer.signedAt}`;
  if (signer.declinedAt) return `Recusou em ${signer.declinedAt}`;
  if (signer.status === 'autoresponded') return 'E-mail não entregue';
  if (signer.deliveredAt) return `Visualizou em ${signer.deliveredAt}, ainda não assinou`;
  return 'Pendente';
};

const uniqueSigners = (signers = []) => {
  const seen = new Set();
  return signers.filter((signer) => {
    const key = `${signer.name}|${signerSituation(signer)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const LastSending = ({ lastSending }) => {
  if (!lastSending) {
    return <p className="text-blueDarker">Nenhum contrato foi enviado para este cliente.</p>;
  }

  const { docusign } = lastSending;
  const kind = normalizeKind(lastSending.kind) || normalizeKind(docusign?.kind);
  const sentAt = lastSending.sentAt || docusign?.sentAt;

  return (
    <div className="flex flex-col gap-1 text-blueDarker">
      {!docusign && (
        <p className="text-sm text-yellow-700 bg-yellow-50 rounded-lg px-3 py-2 mb-2">
          Já houve envio, mas não foi possível consultar o DocuSign agora.
        </p>
      )}
      {docusign && (
        <p>
          <span className="font-semibold">Status:</span> {ENVELOPE_STATUS_LABELS[docusign.status] || docusign.status}
        </p>
      )}
      <p>
        <span className="font-semibold">Enviado em:</span> {sentAt || 'data não registrada'}
      </p>
      {kind && (
        <p>
          <span className="font-semibold">Contrato:</span> {CONTRACT_KIND_LABELS[kind]}
        </p>
      )}
      {docusign?.completedAt && (
        <p>
          <span className="font-semibold">Concluído em:</span> {docusign.completedAt}
        </p>
      )}
      {docusign?.voidedAt && (
        <p>
          <span className="font-semibold">Anulado em:</span> {docusign.voidedAt}
        </p>
      )}
      {docusign?.statusChangedAt && (
        <p>
          <span className="font-semibold">Última atualização:</span> {docusign.statusChangedAt}
        </p>
      )}
      {docusign?.signers?.length > 0 && (
        <div className="mt-2">
          <p className="font-semibold">Assinaturas</p>
          <ul className="text-sm">
            {uniqueSigners(docusign.signers).map((signer) => (
              <li key={`${signer.name}-${signer.routingOrder}-${signer.status}`}>
                {signer.name}: {signerSituation(signer)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const DiscountLine = ({ discount }) => {
  if (!discount) return null;

  if (discount.source === 'provider') {
    return (
      <p>
        Desconto do parceiro {discount.providerName}: {discount.percentage}% → {formatCurrency(discount.finalValue)}
      </p>
    );
  }

  return (
    <p>
      Desconto do cliente: {formatCurrency(discount.amount)} → {formatCurrency(discount.finalValue)}
    </p>
  );
};

const currentPlanNotice = ({ planType, planName }) => {
  if (!planType) {
    return 'O cliente não tem plano definido no cadastro; o contrato padrão sai com o Plano Complexidade 1.';
  }
  if (planType === 'monthly_signature') {
    return `O plano do cliente (${planName}) não existe no contrato; o contrato padrão sai com o Plano Complexidade 1.`;
  }
  return null;
};

const CurrentOptionDescription = ({ option }) => {
  const kind = normalizeKind(option.kind);

  if (kind === 'spouse') {
    return <p>Contrato do Plano Cônjuge (plano atual do cliente), sem plano marcado e sem valor.</p>;
  }

  const notice = currentPlanNotice(option);

  return (
    <>
      <p>
        Contrato padrão com <span className="font-semibold">Plano Complexidade {PLAN_NUMBER[kind]}</span> marcado —
        valor de tabela {formatCurrency(option.tableValue)}
      </p>
      <DiscountLine discount={option.discount} />
      {notice && <p className="text-sm text-yellow-700 bg-yellow-50 rounded-lg px-3 py-2">{notice}</p>}
    </>
  );
};

const OptionCard = ({ title, children, buttonText, onClick, sending }) => (
  <div className="flex-1 flex flex-col justify-between gap-4 border border-bluePale rounded-xl p-5">
    <div className="flex flex-col gap-2 text-blueDarker">
      <p className="font-jost text-lg font-semibold">{title}</p>
      {children}
    </div>
    <Button onClick={onClick} loading={sending} loadingText="Enviando…">
      {buttonText}
    </Button>
  </div>
);

const ContractSendModal = ({ client, onClose, onSent }) => {
  const { addToast } = useToast();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [sending, setSending] = useState(false);
  // trava síncrona: dois cliques no mesmo ciclo do React ainda veem sending = false
  const sendingRef = useRef(false);

  useEffect(() => {
    fetch(`/app/planner/my_clients/${client.id}/contract`, { headers: { Accept: 'application/json' } })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => setData(camelizeKeys(json)))
      .catch((err) => {
        console.error('[ContractSendModal] Erro ao carregar:', err);
        setLoadError(true);
      });
  }, [client.id]);

  const handleSend = (kind) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);

    fetchWithCsrf(`/app/planner/my_clients/${client.id}/resend_contract`, {
      method: 'POST',
      body: JSON.stringify({ kind }),
    })
      .then(async (res) => {
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error(json.message || 'Erro ao enviar contrato.');

        addToast({ type: 'success', message: json.message || 'Contrato enviado com sucesso!' });
        onSent(camelizeKeys(json));
        onClose();
      })
      .catch((err) => {
        console.error('[ResendContract] Erro:', err);
        addToast({ type: 'error', message: err.message });
        sendingRef.current = false;
        setSending(false);
      });
  };

  const handleClose = () => {
    if (!sending) onClose();
  };

  return (
    <Modal onClose={handleClose}>
      <div className="p-10 font-jost">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker text-2xl font-semibold">
            <FileSignature size={24} className="text-tealMedium" strokeWidth={1.25} />
            {client.docusignId ? 'Reenviar contrato' : 'Enviar contrato'}
          </div>
          <button onClick={handleClose} disabled={sending}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        <div className="border-t border-b border-bluePale py-4 mb-6">
          <p className="text-lg text-blueDarker">
            <span className="font-semibold">Cliente:</span> {client.name}
          </p>
        </div>

        {loadError && <p className="text-red-600">Não foi possível carregar as informações do contrato.</p>}

        {!data && !loadError && (
          <div className="flex items-center gap-2 text-blueDarker">
            <LoadingSpinner /> Consultando o último envio…
          </div>
        )}

        {data && (
          <div className="flex flex-col gap-6">
            <section>
              <p className="text-lg font-semibold text-blueDarker mb-2">Último envio</p>
              <LastSending lastSending={data.lastSending} />
            </section>

            <div className="flex flex-col md:flex-row gap-4">
              <OptionCard
                title="Contrato atualmente selecionado"
                buttonText="ENVIAR CONTRATO SELECIONADO"
                onClick={() => handleSend('current')}
                sending={sending}
              >
                <CurrentOptionDescription option={data.options.current} />
              </OptionCard>

              <OptionCard
                title="Contrato cônjuge"
                buttonText="ENVIAR CONTRATO CÔNJUGE"
                onClick={() => handleSend('spouse')}
                sending={sending}
              >
                <p>Contrato do Plano Cônjuge, sem plano marcado e sem valor.</p>
                <p className="text-sm">O plano do cliente no cadastro não muda.</p>
              </OptionCard>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ContractSendModal;
